import { NetworkStatus, gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import AutocompleteWithFetch, { AutoCompleteOption } from 'components/Inputs/AutoCompletewithFetch';
import LoadingIndicator from 'components/LoadingIndicator';
import DataGridTable from 'components/Tables';
import {
  GET_HODs_QUERY,
  GetHODsResponse,
  GetUsersQueryResponse,
  GetUsersQueryVariables,
} from 'graphql/query/user';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { User } from 'types';

const GET_USERS_QUERY = gql`
  query GetUsers($filter: UserFilter) {
    getUsers(filter: $filter) {
      _id
      empId
      name
      email
      department
      designation
      reportingManager {
        _id
        name
      }
    }
  }
`;

const REASSIGN_EMPLOYEES = gql`
  mutation ReassignEmployees($reportingManager: ID, $hodId: ID, $reportees: [ID!]!) {
    reassignEmployees(reportingManager: $reportingManager, hodId: $hodId, reportees: $reportees) {
      _id
    }
  }
`;

type ReassignEmployeeMutationResonse = {
  reassignEmployees: User[];
};
type ReassignEmployeeMutationVariables = {
  reportees: string[];
  hodId?: string;
  reportingManager?: string;
};

const ReassignEmployeeForm = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [reportingManager, setReportingManager] = useState({ _id: '', name: '' });
  const [hod, setHod] = useState({ _id: '', name: '' });

  const {
    data: users,
    refetch: refetchUsers,
    networkStatus: usersNetworkStatus,
  } = useQuery<GetUsersQueryResponse, GetUsersQueryVariables>(GET_USERS_QUERY, {
    notifyOnNetworkStatusChange: true,
  });

  const [reassignEmployees, { loading: reassigningEmployees }] = useMutation<
    ReassignEmployeeMutationResonse,
    ReassignEmployeeMutationVariables
  >(REASSIGN_EMPLOYEES);

  const [getHODs, { loading: loadingHODs, data: hods }] =
    useLazyQuery<GetHODsResponse>(GET_HODs_QUERY);

  const handleSelectionChange = selectionModel => {
    setSelectedIds(selectionModel);
  };

  const loadingUsers = usersNetworkStatus === NetworkStatus.loading;

  const handleSubmit = () => {
    reassignEmployees({
      variables: {
        reportees: selectedIds,
        reportingManager: !!reportingManager._id ? reportingManager._id : undefined,
        hodId: !!hod._id ? hod._id : undefined,
      },
      onCompleted: _ => {
        setSelectedIds([]);
        setReportingManager({ _id: '', name: '' });
        setHod({ _id: '', name: '' });
      },
    });
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Grid container item xs={12} md={10} lg={8} xl={6} rowGap={2} columnSpacing={1.5} mb={3}>
        {loadingUsers ? (
          <LoadingIndicator />
        ) : (
          <>
            <Grid item xs={12}>
              <AutocompleteWithFetch
                value={reportingManager}
                fetch={refetchUsers}
                handleChange={val => setReportingManager(val as AutoCompleteOption)}
                label="Reporting Manager"
                loading={loadingUsers}
                options={users?.getUsers ?? []}
              />
            </Grid>
            <Grid item xs={12}>
              <AutocompleteWithFetch
                value={hod}
                fetch={getHODs}
                handleChange={val => setHod(val as AutoCompleteOption)}
                label="HOD"
                loading={loadingHODs}
                options={hods?.getHODs ?? []}
              />
            </Grid>
            <Grid item xs={12}>
              <DataGridTable
                sx={{
                  maxHeight: '65vh',
                }}
                checkboxSelection
                pagination
                rowSelectionModel={selectedIds}
                onRowSelectionModelChange={handleSelectionChange}
                columns={[
                  {
                    field: 'empId',
                    headerName: 'EmpId',
                    minWidth: 100,
                  },
                  {
                    field: 'name',
                    headerName: 'Employee Name',
                    minWidth: 200,
                  },
                  {
                    field: 'designation',
                    headerName: 'Designation',
                    minWidth: 150,
                  },
                  {
                    field: 'department',
                    headerName: 'Department',
                    minWidth: 200,
                  },
                  {
                    field: 'email',
                    headerName: 'Email',
                    minWidth: 250,
                  },
                  {
                    field: 'reportingManager',
                    headerName: 'Reporting Manager',
                    minWidth: 200,
                    valueFormatter: (val: User) => val.name,
                  },
                ]}
                rows={(users?.getUsers ?? []).filter(user => user._id !== reportingManager._id)}
                getRowId={row => row._id}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} container justifyContent="end">
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={!!!selectedIds.length || (!!!hod?._id && !!!reportingManager?._id)}
            loading={reassigningEmployees}
          >
            Reassign
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default ReassignEmployeeForm;
